import { useState } from "react";
import { Share2, Loader2, Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import { LoginArea } from "@/components/auth/LoginArea";
import { useCurrentUser } from "@/hooks/useCurrentUser";
import { useNostrPublish } from "@/hooks/useNostrPublish";
import { useToast } from "@/hooks/useToast";

interface ShareButtonsProps {
  /** The text to share. */
  text: string;
  /** Optional hashtags for X (without #). */
  hashtags?: string[];
}

// Simple X (Twitter) logo since lucide dropped the twitter icon.
function XLogo({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" className={className} fill="currentColor">
      <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
    </svg>
  );
}

export function ShareButtons({ text, hashtags = ["BitcoinCash", "BCH"] }: ShareButtonsProps) {
  const { user } = useCurrentUser();
  const { mutateAsync: publish, isPending } = useNostrPublish();
  const { toast } = useToast();
  const [posted, setPosted] = useState(false);

  const xUrl = `https://twitter.com/intent/tweet?text=${encodeURIComponent(
    text,
  )}&hashtags=${encodeURIComponent(hashtags.join(","))}`;

  async function shareToNostr() {
    try {
      await publish({
        kind: 1,
        content: `${text}\n\n${hashtags.map((h) => `#${h}`).join(" ")}`,
        tags: hashtags.map((h) => ["t", h.toLowerCase()]),
      });
      setPosted(true);
      toast({
        title: "Shared to Nostr! 🎉",
        description: "Your achievement has been posted.",
      });
    } catch {
      toast({
        title: "Couldn't post to Nostr",
        description: "Please try again in a moment.",
        variant: "destructive",
      });
    }
  }

  return (
    <div className="space-y-3">
      <div className="flex flex-col gap-3 sm:flex-row">
        <Button asChild variant="outline" className="flex-1">
          <a href={xUrl} target="_blank" rel="noopener noreferrer">
            <XLogo className="mr-2 h-4 w-4" /> Share on X
          </a>
        </Button>

        {user ? (
          <Button
            className="flex-1"
            onClick={shareToNostr}
            disabled={isPending || posted}
          >
            {isPending ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : posted ? (
              <Check className="mr-2 h-4 w-4" />
            ) : (
              <Share2 className="mr-2 h-4 w-4" />
            )}
            {posted ? "Shared to Nostr" : "Share on Nostr"}
          </Button>
        ) : (
          <div className="flex-1">
            <LoginArea className="w-full" />
          </div>
        )}
      </div>
      {!user && (
        <p className="text-center text-xs text-muted-foreground">
          Log in with Nostr to share your achievement to the network.
        </p>
      )}
    </div>
  );
}
